import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ThemeService } from '../../../core/services/common/theme.service';
import { AuthService } from '../../../core/services/user/auth.service';
import { UiInteractionService } from '../../../core/services/common/ui-interaction.service';
import { HeaderStatsComponent } from './header-stats.component';
import { MobileHeaderHamburgerComponent } from './mobile-header-hamburger.component';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../../core/services/common/logger.service';

@Component({
  selector: 'app-mobile-header',
  standalone: true,
  imports: [CommonModule, RouterLink, MobileHeaderHamburgerComponent],
  template: `
    <header
      class="fixed top-0 left-0 right-0 z-50 bg-light-primary dark:bg-dark-primary  border-b border-light-border dark:border-dark-border shadow-sm md:hidden"
    >
      <div
        class="container mx-auto px-4 py-2 flex justify-between items-center"
      >
        <!-- Hamburger/menu icon -->
        <button
          (click)="toggleMenu()"
          class="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <!-- three horizontal dots icon -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 8h16M4 16h16"
            />
          </svg>
        </button>

        <!-- Logo only -->
        <a [routerLink]="['/']" class="flex items-center">
          <img src="favicon.ico" alt="Fact Check" class="h-8 w-8" />
        </a>

        <!-- Dark/light toggle -->
        <button
          (click)="toggleDarkMode()"
          class="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <svg
            *ngIf="isDarkMode"
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <svg
            *ngIf="!isDarkMode"
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-gray-700"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        </button>
      </div>

      <!-- Full-screen menu overlay -->
      <app-mobile-header-hamburger
        [showMenu]="showMenu"
        (close)="toggleMenu()"
        (continueWithGoogle)="continueWithGoogle()"
      ></app-mobile-header-hamburger>

      <!-- Sign in error message -->
      <div
        *ngIf="signInError"
        class="fixed bottom-4 left-4 right-4 bg-red-500 text-white p-3 rounded shadow-lg text-sm z-60"
      >
        {{ signInError }}
        <button (click)="signInError = null" class="float-right font-bold">
          X
        </button>
      </div>
    </header>
  `,
  styles: [],
})
export class MobileHeaderComponent implements OnInit, OnDestroy {
  isDarkMode = false;
  showMenu = false;
  signInLoading = false;
  signInError: string | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private themeService: ThemeService,
    public auth: AuthService,
    private uiInteractionService: UiInteractionService,
    private logger: LoggerService,
  ) {
    this.themeService.darkMode$.subscribe(d => (this.isDarkMode = d));
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.uiInteractionService.requestSignInDialog$.subscribe(() => {
        if (!this.showMenu) {
          this.toggleMenu(); // Open the menu when sign-in is requested
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  toggleDarkMode() {
    this.themeService.toggleDarkMode();
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
    if (!this.showMenu) {
      this.signInError = null;
    }
  }

  continueWithGoogle() {
    this.signInLoading = true;
    this.signInError = null;
    this.auth
      .signInWithGoogle()
      .then(() => {
        // Success: Navigation is handled by AuthService or user stays on page
        // No action needed here besides resetting loading state
      })
      .catch(error => {
        this.logger.error('Error during mobile Google sign-in:', error);
        this.signInError = 'Sign-in failed. Please try again.';
      })
      .finally(() => {
        this.signInLoading = false;
      });
  }

  continueWithApple() {
    this.signInLoading = true;
    this.signInError = null;
    this.auth
      .signInWithApple()
      .then(() => {
        // Success: Navigation handled by AuthService
      })
      .catch(error => {
        this.logger.error('Error during mobile Apple sign-in:', error);
        this.signInError =
          typeof error === 'string'
            ? error
            : 'Apple Sign-in failed. Please try again.';
      })
      .finally(() => {
        this.signInLoading = false;
      });
  }
}
